import { DocumentInput, NameInput } from "./general";
import {
  OptInTextWithLinks,
  PrintConfig,
  UserDetailsConfig,
  KeepsakeLabels,
  KeepsakeOfferings,
  MyMomentsDefaultKeepsake,
  EnhancedGeneralAdmissionTeamSettings,
} from "./team";

export enum TEMPLATE_TYPES {
  MAIN = "momentTemplate",
  GAME_MAIN = "ticketTemplate",
  FAN1st = "fan1stTicketTemplate",
  SPECIAL_SECTION = "specialSectionTicketTemplate",
  SPECIAL_1 = "specialTicketTemplate1",
  SPECIAL_2 = "specialTicketTemplate2",
  SPECIAL_3 = "specialTicketTemplate3",
  SPECIAL_4 = "specialTicketTemplate4",
  SPECIAL_5 = "specialTicketTemplate5",
  SPECIAL_6 = "specialTicketTemplate6",
}

/** Winner from operator final score entry (team1 / team2 are game document home/away slots). */
export type GameFinalWinner = "team1" | "team2" | "tie";

export interface GameInput {
  team1: string;
  team2: string;
  stadium: string;
  league: string;
  startDate: number;
  finishDate?: number;
  archived?: boolean;
  /** Official final score when operator finishes the game checklist. */
  finalTeam1Score?: number;
  finalTeam2Score?: number;
  finalWinner?: GameFinalWinner;
  seatingConfiguration?: string; // Reference to the seating configuration used for this game

  ticketTemplate?: string;
  template?: TicketTemplate;
  ticketTemplateImageKey?: string;
  fan1stTicketTemplate?: string;
  fan1stTemplate?: TicketTemplate;
  fan1stTicketTemplateImageKey?: string;
  specialSectionTicketTemplate?: string;
  specialSectionTemplate?: TicketTemplate;
  specialSectionTicketTemplateImageKey?: string;
  specialTicketTemplate1?: string;
  specialTemplate1?: TicketTemplate;
  specialTicketTemplateImageKey1?: string;
  specialTicketTemplate2?: string;
  specialTemplate2?: TicketTemplate;
  specialTicketTemplateImageKey2?: string;
  specialTicketTemplate3?: string;
  specialTemplate3?: TicketTemplate;
  specialTicketTemplateImageKey3?: string;
  specialTicketTemplate4?: string;
  specialTemplate4?: TicketTemplate;
  specialTicketTemplateImageKey4?: string;
  specialTicketTemplate5?: string;
  specialTemplate5?: TicketTemplate;
  specialTicketTemplateImageKey5?: string;
  specialTicketTemplate6?: string;
  specialTemplate6?: TicketTemplate;
  specialTicketTemplateImageKey6?: string;

  paidGame?: boolean;
  showPrintOrderButton?: boolean;
  checkLastName?: boolean;
  devGame?: boolean;
  teamSecondaryLogoColorDuoId?: string;
  /** Game-level default sponsor overlay id; falls back to team default when unset. */
  gameDefaultSponsorOverlay?: string;
  sponsorFramesHiddenForGame?: string[];
  sponsorFramesRestoredForGame?: string[];
  gameContext?: string;
  startTime?: string;
  snapSquadArrivalTime?: string;
  snapSquadFinishTime?: string;
  operatorArrivalTime?: string;
  booth1ArrivalTime?: string;
  booth2ArrivalTime?: string;
  assignedOperators?: string[];
  /** Super admin game manager emails (normalized). */
  gameManagers?: string[];
  /** @deprecated Use gameManagers; kept for legacy documents. */
  gameManager?: string;
  season?: string;
  /** Optional grouping of consecutive home games for engagement/scheduling. */
  homestand?: string;
  /** Operator-entered venue attendance for engagement reporting. */
  engagementAttendance?: number | null;
  /**
   * Frozen kickoff-day weather once the game is finished.
   * Upcoming/ongoing weather is fetched live and not stored here.
   */
  weatherSnapshot?: {
    city?: string;
    state?: string;
    timezone?: string;
    atMs?: number;
    temperatureF?: number;
    precipitationChancePct?: number;
    weatherCode?: number;
    summary?: string;
    icon?: string;
    sunsetMs?: number | null;
    frozen?: boolean;
    frozenAt?: number;
  } | null;
  /**
   * Cached fan games-card Photos badge. Refreshed on publish; list reads this
   * instead of aggregating per request.
   */
  publishedPhotosCount?: number;
}

export interface UpdateGameRequest {
  team1?: string;
  team2?: string;
  stadium?: string;
  league?: string;
  startDate?: number;
  archived?: boolean;
  seatingConfiguration?: string;

  ticketTemplate?: string;
  template?: TicketTemplate;
  ticketTemplateImageKey?: string;
  fan1stTicketTemplate?: string;
  fan1stTemplate?: TicketTemplate;
  fan1stTicketTemplateImageKey?: string;
  specialSectionTicketTemplate?: string;
  specialSectionTemplate?: TicketTemplate;
  specialSectionTicketTemplateImageKey?: string;
  specialTicketTemplate1?: string;
  specialTemplate1?: TicketTemplate;
  specialTicketTemplateImageKey1?: string;
  specialTicketTemplate2?: string;
  specialTemplate2?: TicketTemplate;
  specialTicketTemplateImageKey2?: string;
  specialTicketTemplate3?: string;
  specialTemplate3?: TicketTemplate;
  specialTicketTemplateImageKey3?: string;
  specialTicketTemplate4?: string;
  specialTemplate4?: TicketTemplate;
  specialTicketTemplateImageKey4?: string;
  specialTicketTemplate5?: string;
  specialTemplate5?: TicketTemplate;
  specialTicketTemplateImageKey5?: string;
  specialTicketTemplate6?: string;
  specialTemplate6?: TicketTemplate;
  specialTicketTemplateImageKey6?: string;

  paidGame?: boolean;
  showPrintOrderButton?: boolean;
  checkLastName?: boolean;
  devGame?: boolean;
  gameDefaultSponsorOverlay?: string | null;
  sponsorFramesHiddenForGame?: string[];
  sponsorFramesRestoredForGame?: string[];
  gameContext?: string;
  startTime?: string | null;
  snapSquadArrivalTime?: string | null;
  snapSquadFinishTime?: string | null;
  operatorArrivalTime?: string | null;
  booth1ArrivalTime?: string | null;
  booth2ArrivalTime?: string | null;
  season?: string;

  /**
   * Super-admin only: requires `viewerEmail` query param on PUT. Use `null` for all three
   * to clear official finals on the game document.
   */
  finalTeam1Score?: number | null;
  finalTeam2Score?: number | null;
  finalWinner?: GameFinalWinner | null;
}

export interface SetFinishDateRequest {
  finishDate?: number;
  archived?: boolean;
  finalTeam1Score?: number;
  finalTeam2Score?: number;
  finalWinner?: GameFinalWinner;
}

export interface GetIntervalGames {
  start: number;
  recentGames?: boolean;
  count?: number;
  /** Home team ObjectId — scopes fan recent-games to that club. */
  teamId?: string;
  /** Fan hostname — scopes to all teams with this hostname (multi-club hosts). */
  hostname?: string;
  /** Page size for recentGames (default 20, max 40). */
  limit?: number;
  /** Skip for recentGames pagination. */
  offset?: number;
  archived?: boolean | string;
  excludeTemplates?: boolean;
  includeDevGames?: boolean;
}

export interface IntervalGamePopulated extends DocumentInput {
  state: string;
  city: string;
  stadium: string;
  stadiumSchema?: string;
  startDate: number;
  team1: TeamGamePopulated;
  team2: TeamGamePopulated;
  finishDate?: number;
  archived?: boolean;
  stadiumId: string;
  sportName?: string;
  /** Populated league name for the game (e.g. "MLB"); used for fan legal footer gating. */
  leagueName?: string;

  lastMomentTeam1Score?: number;
  lastMomentTeam2Score?: number;
  finalTeam1Score?: number;
  finalTeam2Score?: number;
  finalWinner?: GameFinalWinner;
  lastMomentTime?: string;
  lastMomentPeriodType?: string;
  lastMomentPeriodText?: string;
  publishedMomentsCount?: number;
  publishedPhotosCount?: number;

  /** Displayed kickoff time string (operator "Displayed Start Time"); optional */
  startTime?: string;
  /** Set when an operator marks the game live from the dashboard (see mark-operator-game-live). */
  operatorMarkedLiveAt?: number;
  gameContext?: string;
  seasonName?: string;
  /** Display order for fan games page season chips; lower = first */
  seasonSortOrder?: number;

  ticketTemplate?: string;
  template?: TicketTemplate;
  ticketTemplateImageKey?: string;
  fan1stTicketTemplate?: string;
  fan1stTemplate?: TicketTemplate;
  fan1stTicketTemplateImageKey?: string;
  specialSectionTicketTemplate?: string;
  specialSectionTemplate?: TicketTemplate;
  specialSectionTicketTemplateImageKey?: string;
  specialTicketTemplate1?: string;
  specialTemplate1?: TicketTemplate;
  specialTicketTemplateImageKey1?: string;
  specialTicketTemplate2?: string;
  specialTemplate2?: TicketTemplate;
  specialTicketTemplateImageKey2?: string;
  specialTicketTemplate3?: string;
  specialTemplate3?: TicketTemplate;
  specialTicketTemplateImageKey3?: string;
  specialTicketTemplate4?: string;
  specialTemplate4?: TicketTemplate;
  specialTicketTemplateImageKey4?: string;
  specialTicketTemplate5?: string;
  specialTemplate5?: TicketTemplate;
  specialTicketTemplateImageKey5?: string;
  specialTicketTemplate6?: string;
  specialTemplate6?: TicketTemplate;
  specialTicketTemplateImageKey6?: string;

  paidGame?: boolean;
  showPrintOrderButton?: boolean;
  checkLastName?: boolean;
  teamSecondaryLogoColorDuoId?: string;
  gameDefaultSponsorOverlay?: string;
  sponsorFramesHiddenForGame?: string[];
  sponsorFramesRestoredForGame?: string[];
}

export interface TeamGamePopulated {
  _id: string;
  name: string;
  logo: string;
  /** 3-letter abbreviation for ticket templates / operator displays. */
  teamAbbreviation?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  gamesCardLogoBackgroundColor?: string;
  paywallImage?: string;
  ticketAdImage?: string;
  popupImage?: string;
  hidePurchase?: boolean;
  purchasePopupAfterDownloadedImage?: boolean;
  fanSharingPromptEnabled?: boolean;
  gamedayStoryPromptEnabled?: boolean;
  hideDownload?: boolean;
  showOrderPrintedPhoto?: boolean;
  showWatermark?: boolean;
  watermarkOptInConfig?: import("./team").WatermarkOptInConfig;
  momentScoreboardFanEnabled?: boolean;
  momentScoreboardHideTeam1Logo?: boolean;
  momentScoreboardIncludeTeamScores?: boolean;
  gamesCardIncludeTeamScores?: boolean;
  /** Home (team1) on top of game cards and left on scoreboard. Default off. */
  homeTeamOnTop?: boolean;
  analyticsTrackingEnabled?: boolean;
  automatedDataRuns?: boolean;
  enablePaidDownload?: boolean;
  profileEnabled?: boolean;
  printConfig?: PrintConfig;
  userDetailsConfig?: UserDetailsConfig;
  customTeamOptInText?: OptInTextWithLinks;
  customSponsorOptInText?: OptInTextWithLinks;
  customThirdOptInText?: OptInTextWithLinks;
  customFanIdOptInText?: OptInTextWithLinks;
  customConsentText?: OptInTextWithLinks;
  keepsakeLabels?: KeepsakeLabels;
  keepsakeOfferings?: KeepsakeOfferings;
  shareHashtag?: string;
  hostname?: string;
  /** US or EU date format inherited from the home team. Default us. */
  teamRegion?: import("./team").TeamRegion;
  myMomentsDefaultKeepsake?: MyMomentsDefaultKeepsake;
  /** Home-team default ticket when the game has no `ticketTemplate`. */
  defaultTicketTemplate?: string;
  dataMomentCaptions?: string[];
  /** Operator notes shown with the data moment script on the Moments list. */
  dataMomentInformation?: string;
  seatEntryLimitRegular?: number;
  seatEntryLimitGA?: number;
  sendToVideoboard?: boolean;
  /** Sit/Stand Shifting: require Sitting/Standing on moments; shift standing blur. */
  sitStandShiftingEnabled?: boolean;
  standingBlurShiftPixels?: number;
  /** When on, fan side menu includes Opt Out. Default off. */
  menuBarOptOutEnabled?: boolean;
  /** When on, fans can opt a seat out of the entire season. Default off. */
  seasonLongOptOutEnabled?: boolean;
  /** Fan Surname verification after seat selection. Default off. */
  surnameVerificationEnabled?: boolean;
  /** Under 18 Flow: 14–17 surname unblur. Default off. */
  under18FlowEnabled?: boolean;
  /** Collect fan details before game selection (lead gen first flow). */
  leadGenFirst?: boolean;
  enhancedGeneralAdmission?: EnhancedGeneralAdmissionTeamSettings;
  /** Sponsor frames hidden on this team's unfinished games. */
  sponsorFramesHiddenFromUnfinished?: string[];
}

export interface TeamInputOperator extends DocumentInput, TeamGamePopulated {}

export interface OperatorGamePopulated extends DocumentInput {
  team1: TeamInputOperator;
  team2: TeamInputOperator;
  state: NameInput;
  city: NameInput;
  stadium: NameInput;
  sport: NameInput;
  league: NameInput;
  seasonName?: string;
  seasonSortOrder?: number;
  /** Homestand id, or populated `{ _id }` from list APIs. */
  homestand?: string | { _id: string } | null;
  startDate: number;
  /** Full weekday name for `startDate` in local time (e.g. "Wednesday"); computed for API
   *  responses. */
  startDateWeekday?: string;
  finishDate?: number;
  archived?: boolean;
  draftMoments?: number;
  uniqueCamerasTriggered?: number;

  ticketTemplate?: string;
  template?: TicketTemplate;
  ticketTemplateImageKey?: string;
  fan1stTicketTemplate?: string;
  fan1stTemplate?: TicketTemplate;
  fan1stTicketTemplateImageKey?: string;
  specialSectionTicketTemplate?: string;
  specialSectionTemplate?: TicketTemplate;
  specialSectionTicketTemplateImageKey?: string;
  specialTicketTemplate1?: string;
  specialTemplate1?: TicketTemplate;
  specialTicketTemplateImageKey1?: string;
  specialTicketTemplate2?: string;
  specialTemplate2?: TicketTemplate;
  specialTicketTemplateImageKey2?: string;
  specialTicketTemplate3?: string;
  specialTemplate3?: TicketTemplate;
  specialTicketTemplateImageKey3?: string;
  specialTicketTemplate4?: string;
  specialTemplate4?: TicketTemplate;
  specialTicketTemplateImageKey4?: string;
  specialTicketTemplate5?: string;
  specialTemplate5?: TicketTemplate;
  specialTicketTemplateImageKey5?: string;
  specialTicketTemplate6?: string;
  specialTemplate6?: TicketTemplate;
  specialTicketTemplateImageKey6?: string;

  paidGame?: boolean;
  showPrintOrderButton?: boolean;
  checkLastName?: boolean;
  devGame?: boolean;
  seatingConfiguration?: NameInput;
  teamSecondaryLogoColorDuoId?: string;
  /** Resolved preview URL when a secondary duo is active (operator). */
  secondaryLogo?: string;
  gameDefaultSponsorOverlay?: string;
  sponsorFramesHiddenForGame?: string[];
  sponsorFramesRestoredForGame?: string[];
  gameContext?: string;
  startTime?: string;
  snapSquadArrivalTime?: string;
  snapSquadFinishTime?: string;
  operatorArrivalTime?: string;
  booth1ArrivalTime?: string;
  booth2ArrivalTime?: string;
  operatorMarkedLiveAt?: number;
  assignedOperators?: string[];
  gameManagers?: string[];
  gameManager?: string;
  finalTeam1Score?: number;
  finalTeam2Score?: number;
  finalWinner?: GameFinalWinner;
  lastMomentTeam1Score?: number;
  lastMomentTeam2Score?: number;
  lastMomentPeriodText?: string;
}

export interface GetGameListRequest {
  sport?: string;
  state?: string;
  city?: string;
  league?: string;
  stadium?: string;
  team?: string;
  /** When true with `team`, match only home games (team1). Default is home or away. */
  homeTeamOnly?: boolean | string;
  date?: number;
  archived?: boolean | string;
  operatorEmail?: string;
  /** Page size for all-time operator lists (Games page Load more). */
  limit?: number | string;
  /** Skip this many newest-first rows when paging all-time lists. */
  offset?: number | string;
}

export interface TemplateTextSection {
  hidden?: boolean;
  /**
   * When true on section or row (mutually exclusive), General Admission tickets
   * place the GA section name in that box instead of the legacy row-or-section heuristic.
   */
  gaPlacement?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  backgroundColor?: string;
  color?: string;
  font?: string;
  fontSize?: string;
  fontBold?: string;
}

export interface TemplateImageSection {
  hidden?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TicketTemplateFields {
  team?: string;
  teamId?: string;
  name: string;
  templateImageKey: string;
  templateImage?: string;
  imagePosition?: TemplateImageSection;
  imageScale?: number;
  section?: TemplateTextSection;
  row?: TemplateTextSection;
  seat?: TemplateTextSection;
  scoreTeam1?: TemplateTextSection;
  scoreTeam2?: TemplateTextSection;
  momentText?: TemplateTextSection;
  date?: TemplateTextSection;
  stadium?: TemplateTextSection;
  city?: TemplateTextSection;
  team1Abbreviation?: TemplateTextSection;
  team2Abbreviation?: TemplateTextSection;
  team1Image?: TemplateImageSection;
  team2Image?: TemplateImageSection;
}

export interface TicketTemplate extends TicketTemplateFields {
  _id?: string;
}

/** Placement-only snapshot (no name, image key, or ids). */
export type TicketTemplatePlacement = Partial<
  Pick<
    TicketTemplateFields,
    | "imagePosition"
    | "imageScale"
    | "section"
    | "row"
    | "seat"
    | "scoreTeam1"
    | "scoreTeam2"
    | "momentText"
    | "date"
    | "stadium"
    | "city"
    | "team1Abbreviation"
    | "team2Abbreviation"
    | "team1Image"
    | "team2Image"
  >
>;

export interface OperatorGameSeat extends DocumentInput {
  section?: string;
  row?: string;
  seat?: string;
  lastName?: string;
  birthDate?: string;
  classification?: string;
  fanId?: string;
  email?: string;
  isSeasonTicketHolder?: boolean;
  optOutSource?: "manual" | "birthdate";
  gameId?: string;
  linkedSeats?: string[];

  middleSeat?: string;
  ticket?: string;
  ticketURL?: string;
  imageURL?: string;
  customURL?: string;
}

export interface SeatInput {
  section?: string;
  row?: string;
  seat?: string;
  lastName?: string;
  birthDate?: string;
  gameId?: string;
  linkedSeats?: string[];
  middleSeat?: string;
  ticket?: string;
  /** 14–17 Under 18 Flow: after surname match, remove this seat from the game Opt-Out list. */
  unblurOptOut?: boolean;
}

export interface SeatGroupInput {
  section?: string;
  row?: string;
  seat?: string;
  linkedSeats?: string[];
  gameId?: string;
  middleSeat?: string;
  ticket?: string;
  ticketURL?: string;
  imageURL?: string;
  customURL?: string;
}

export interface UpdateSeatGroupUniqueUrl {
  _id: string;
  ticketURL?: string;
  imageURL?: string;
  customURL?: string;
}
